import { useCallback, useEffect, useRef, useState } from 'react'
import { CheckCircle, Gift, ShareNetwork, Sparkle } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import {
  ApiError,
  getReferral,
  getRewardsStatus,
  postCheckin,
  postRelief,
  type ReferralInfo,
  type RewardsStatus,
} from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { LoadNotice } from '../components/LoadNotice'
import { ReferralActions } from '../components/ReferralActions'

export function Tasks() {
  const { t } = useTranslation()
  const { token, refreshBalance } = useAuth()
  const [rewards, setRewards] = useState<RewardsStatus | null>(null)
  const [referral, setReferral] = useState<ReferralInfo | null>(null)
  const [busy, setBusy] = useState<'checkin' | 'relief' | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const mounted = useRef(true)
  const loadVersion = useRef(0)
  const claimInFlight = useRef(false)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const reload = useCallback(async () => {
    if (!token) return
    const version = ++loadVersion.current
    setLoading(true)
    setLoadError('')
    const [rewardsResult, referralResult] = await Promise.allSettled([
      getRewardsStatus(token),
      getReferral(token),
    ])
    if (!mounted.current || version !== loadVersion.current) return
    if (rewardsResult.status === 'fulfilled') setRewards(rewardsResult.value)
    if (referralResult.status === 'fulfilled') setReferral(referralResult.value)
    if (rewardsResult.status === 'rejected' || referralResult.status === 'rejected') {
      setLoadError(t('tasks.loadError'))
    }
    setLoading(false)
    await refreshBalance()
  }, [refreshBalance, t, token])

  useEffect(() => {
    void reload()
  }, [reload])

  const claim = async (kind: 'checkin' | 'relief') => {
    if (!token || busy || claimInFlight.current) return
    claimInFlight.current = true
    setBusy(kind)
    setError('')
    try {
      if (kind === 'checkin') await postCheckin(token)
      else await postRelief(token)
      await reload()
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : 'request_failed'
      setError(t(`errors.${code}`, { defaultValue: t('errors.request_failed') }))
    } finally {
      claimInFlight.current = false
      setBusy(null)
    }
  }

  return (
    <div className="page page-tasks" aria-busy={loading}>
      <header className="page-heading">
        <span className="page-kicker">ATLAS MISSIONS</span>
        <h2>{t('tasks.title')}</h2>
        <p>{t('tasks.subtitle')}</p>
      </header>

      {loadError ? (
        <LoadNotice message={loadError} onRetry={() => void reload()} busy={loading} />
      ) : null}

      <section className="mission-card mission-card--daily">
        <span className="mission-icon"><Sparkle weight="fill" /></span>
        <span className="mission-copy">
          <strong>{t('profile.checkin')}</strong>
          <small>{t('tasks.dailyHint')}</small>
        </span>
        <span className="mission-reward">
          {rewards ? `+${rewards.checkin_amount}` : '—'}
        </span>
        <button
          type="button"
          className="btn"
          disabled={!rewards?.checkin_available || busy !== null}
          onClick={() => void claim('checkin')}
        >
          {!rewards
            ? t('app.dataUnavailable')
            : rewards.checkin_available
              ? t('profile.claimCheckin')
              : t('profile.checkinDone')}
        </button>
      </section>

      <section className="mission-card">
        <span className="mission-icon"><Gift weight="fill" /></span>
        <span className="mission-copy">
          <strong>{t('profile.relief')}</strong>
          <small>{t('tasks.reliefHint')}</small>
        </span>
        <span className="mission-reward">
          {rewards ? `+${rewards.relief_amount}` : '—'}
        </span>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={!rewards?.relief_available || busy !== null}
          onClick={() => void claim('relief')}
        >
          {!rewards
            ? t('app.dataUnavailable')
            : rewards.relief_available
              ? t('profile.claimRelief')
              : t('profile.reliefUnavailable')}
        </button>
      </section>

      <section className="mission-card">
        <span className="mission-icon"><ShareNetwork weight="fill" /></span>
        <span className="mission-copy">
          <strong>{t('profile.inviteTitle')}</strong>
          <small>
            {referral
              ? t('tasks.inviteHint', { count: referral.invitees_count })
              : t('tasks.inviteUnavailable')}
          </small>
        </span>
        <span className="mission-reward">
          {referral ? `+${referral.rewards_total}` : '—'}
        </span>
        <span className="mission-code">{referral?.start_payload || referral?.code || '—'}</span>
        <ReferralActions code={referral?.start_payload || referral?.code || ''} />
      </section>

      <p className="task-status">
        <CheckCircle weight="fill" />
        {t('tasks.fairNotice')}
      </p>
      {error ? <p className="error" role="alert">{error}</p> : null}
    </div>
  )
}
